import { FC, ReactElement } from 'react';

import { CartSidebar } from '../../containers';
import { ProductQuantitySubmitFormData } from '../../containers/@forms/CartItemForm/validations';
import { GTMEvent } from '../../entities/GTM/GTM';
import { generateCartItemToGTMProduct } from '../../entities/GTM/GTMRequests';
import { sendGTMEvent } from '../../entities/GTM/GTMService';
import { ToastInterface } from '../../entities/Toast/Toast';
import { MOCK_USPS } from '../../mock/mock-data/usp';
import { removeProductVariantFromCart, updateProductVariantInCart } from '../../redux/@shop/cart/cartActions';
import { setIsSuccessfullyAdded } from '../../redux/@shop/cart/cartSlice';
import { addProductVariantToWishlist, removeProductVariantFromWishlist } from '../../redux/@shop/wishlist/wishlistActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';
import { addPositiveToast } from '../../redux/toast/toastActions';
import { AtLeast } from '../../types';

export const ConnectedCartSideBar: FC = (): ReactElement => {
    const dispatch = useTypedDispatch();

    const cart = useTypedSelector(state => state.cartSlice.cart);
    const isInitiallyLoading = useTypedSelector(state => state.cartSlice.isInitiallyLoading);
    const isLoading = useTypedSelector(state => state.cartSlice.isLoading);
    const productIsAddedToCart = useTypedSelector(state => state.cartSlice.isSuccessfulAdded);

    const wishlist = useTypedSelector(state => state.wishlistSlice.wishlist);

    const handleShowMobileProductIsAddedToCart = (toast: AtLeast<ToastInterface, 'title'>): void => {
        dispatch(addPositiveToast(toast));
    };

    const handleUpdateCartItemQuantity = (formData: ProductQuantitySubmitFormData): void => {
        dispatch(updateProductVariantInCart(formData));
    };

    const handleRemoveItemFromCart = (id: string): void => {
        dispatch(removeProductVariantFromCart(id));

        const cartItem = cart.items.find(item => item.id === id);

        if (!cartItem) {
            return;
        }

        const gtmProduct = generateCartItemToGTMProduct(cartItem);

        sendGTMEvent(GTMEvent.removeItemFromCart, gtmProduct);
    };

    const handleWishlistToggle = async (productVariantId: string, isInWishlist: boolean): Promise<void> => {
        if (isInWishlist) {
            await dispatch(removeProductVariantFromWishlist(productVariantId));
            return;
        }

        await dispatch(addProductVariantToWishlist(productVariantId));

        const cartItem = cart.items.find(item => item.variantId === productVariantId);

        if (!cartItem) {
            return;
        }

        const gtmProduct = generateCartItemToGTMProduct(cartItem);

        sendGTMEvent(GTMEvent.addToWishlist, gtmProduct);
    };

    const handleResetProductIsAddedToCart = (): void => {
        dispatch(setIsSuccessfullyAdded(false));
    };

    return (
        <CartSidebar
            {...cart}
            isInitiallyLoading={isInitiallyLoading}
            isLoading={isLoading}
            productIsAddedToCart={productIsAddedToCart}
            wishlistItems={wishlist.variantIds}
            uspItems={MOCK_USPS} // TODO: Replace with data from Strapi
            onShowMobileProductIsAddedToCart={handleShowMobileProductIsAddedToCart}
            onRemoveItemFromCart={handleRemoveItemFromCart}
            onUpdateCartItem={handleUpdateCartItemQuantity}
            onUpdateCartItemIsInWishlist={handleWishlistToggle}
            onResetProductIsAddedToCart={handleResetProductIsAddedToCart}
        />
    );
};
